import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

// Pages
import Home from '@/pages/Home'
import Dashboard from '@/pages/Dashboard'
import Login from '@/pages/Auth/Login'
import GlobalLogin from '@/pages/Auth/GlobalLogin'
import Register from '@/pages/Auth/Register'
import SelectOrg from '@/pages/Auth/SelectOrg'
import SetPassword from '@/pages/Auth/SetPassword'
import SlackCallback from '@/pages/Auth/SlackCallback'
import SlackInstallCallback from '@/pages/Auth/SlackInstallCallback'
import Timesheet from '@/pages/Timesheet'
import Leaves from '@/pages/Leaves'
import UserSettings from '@/pages/UserSettings'
import { Privacy, Terms, Support } from '@/pages/Legal'
import Landing from '@/pages/Landing'
import PlatformConsole from '@/pages/Platform/Console'
import { usePlatformStore } from '@/stores/platformStore'

// Admin pages
import AdminDashboard from '@/pages/Admin/Dashboard'
import AdminAttendance from '@/pages/Admin/Attendance'
import AdminSettings from '@/pages/Admin/Settings'
import Users from '@/pages/Admin/Users'
import UserDetail from '@/pages/Admin/UserDetail'
import Inventory from '@/pages/Admin/Inventory'
import Organization from '@/pages/Admin/Organization'
import Payroll from '@/pages/Admin/Payroll'
import HRAnalytics from '@/pages/Admin/HRAnalytics'
import EmployeeAttendance from '@/pages/Admin/EmployeeAttendance'
import Roles from '@/pages/Admin/Roles'
import ComingSoon from '@/components/ComingSoon'
import { useMyCaps } from '@/lib/capabilities'
import Payslips from '@/pages/Payslips'
import Requests from '@/pages/Requests'
import Approvals from '@/pages/Approvals'
import ManageLeaves from '@/pages/ManageLeaves'
import Invoices from '@/pages/Invoices'
import PublicAssetPage from '@/pages/PublicAsset'

// Layout
import Layout from '@/components/layout/Layout'


function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, org_slug } = useAuthStore()
  const { orgSlug } = useParams<{ orgSlug: string }>()

  if (!isAuthenticated) {
    return <Navigate to={`/${orgSlug}/login`} replace />
  }

  if (org_slug && orgSlug && org_slug !== orgSlug) {
    const currentPath = window.location.pathname
    const afterSlug = currentPath.replace(`/${orgSlug}`, '')
    return <Navigate to={`/${org_slug}${afterSlug}`} replace />
  }

  return <>{children}</>
}

// Capability-gated route. Built-in roles resolve synchronously; custom roles
// wait for their capabilities to load.
function CapRoute({ anyOf, children }: { anyOf: string[]; children: React.ReactNode }) {
  const { org_slug } = useAuthStore()
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { has, loading } = useMyCaps()
  if (loading) return <div className="p-10 text-center text-sm text-gray-400">Loading…</div>
  if (!anyOf.some((c) => has(c))) return <Navigate to={`/${orgSlug || org_slug}`} replace />
  return <>{children}</>
}

// Full admin (or a custom role with Admin Settings)
function AdminRoute({ children }: { children: React.ReactNode }) {
  return <CapRoute anyOf={['admin_settings']}>{children}</CapRoute>
}
// Elevated staff: user/inventory/payroll/HR management (was admin OR accounts)
function StaffRoute({ children }: { children: React.ReactNode }) {
  return <CapRoute anyOf={['manage_users', 'manage_inventory', 'manage_payroll', 'manage_hr']}>{children}</CapRoute>
}
// HR analytics portal
function HRRoute({ children }: { children: React.ReactNode }) {
  return <CapRoute anyOf={['view_analytics']}>{children}</CapRoute>
}
// Attendance roster & approvals
function AttendanceRoute({ children }: { children: React.ReactNode }) {
  return <CapRoute anyOf={['manage_attendance']}>{children}</CapRoute>
}
// HR dashboard (KPI overview) — full admins or anyone with HR analytics access
function HRDashboardRoute({ children }: { children: React.ReactNode }) {
  return <CapRoute anyOf={['admin_settings', 'view_analytics']}>{children}</CapRoute>
}

// Index route: admins/HR land on the HR dashboard, everyone else on the
// employee home page. Mirrors CapRoute's loading handling so it doesn't
// flash the wrong landing page while capabilities are still resolving.
function IndexRoute() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const { has, loading } = useMyCaps()
  if (loading) return <div className="p-10 text-center text-sm text-gray-400">Loading…</div>
  if (has('admin_settings') || has('view_analytics')) {
    return <Navigate to={`/${orgSlug}/admin/dashboard`} replace />
  }
  return <Home />
}

function PlatformRoute({ children }: { children: React.ReactNode }) {
  const { isPlatformAuthed } = usePlatformStore()
  if (!isPlatformAuthed) return <Navigate to="/login" replace />
  return <>{children}</>
}

function App() {
  return (
    <Routes>
      {/* Home = public marketing landing page (Slack Marketplace requirement) */}
      <Route path="/" element={<Landing />} />

      {/* Public legal / support pages (required for Slack app directory) */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />

      {/* Slack OAuth callback */}
      <Route path="/auth/slack/callback" element={<SlackCallback />} />
      <Route path="/slack/install/callback" element={<SlackInstallCallback />} />

      {/* Global sign-in: email+password / Slack SSO + org picker (no auth) */}
      <Route path="/login" element={<GlobalLogin />} />
      {/* Public organization registration */}
      <Route path="/register" element={<Register />} />

      {/* Platform super-admin console (cross-tenant) */}
      <Route path="/admin/platform" element={<PlatformRoute><PlatformConsole /></PlatformRoute>} />
      <Route path="/select-org" element={<SelectOrg />} />
      <Route path="/set-password" element={<SetPassword />} />

      {/* Org-scoped login */}
      <Route path="/:orgSlug/login" element={<Login />} />

      {/* Public read-only asset page a QR label points to (no auth) */}
      <Route path="/:orgSlug/i/:assetTag" element={<PublicAssetPage />} />

      {/* Org-scoped protected routes */}
      <Route
        path="/:orgSlug"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* User routes */}
        <Route index element={<IndexRoute />} />
        <Route path="attendance" element={<Dashboard />} />
        <Route path="timesheet" element={<Timesheet />} />
        <Route path="leaves" element={<Leaves />} />
        <Route path="settings" element={<UserSettings />} />
        <Route path="payslips" element={<Payslips />} />
        <Route path="requests" element={<Requests />} />
        <Route path="approvals" element={<Approvals />} />
        {/* Personal dashboard for admins/HR, whose landing page is the HR dashboard */}
        <Route path="me" element={<Home />} />

        {/* Admin routes */}
        <Route path="admin/dashboard" element={<HRDashboardRoute><AdminDashboard /></HRDashboardRoute>} />
        <Route path="admin/attendance" element={<AttendanceRoute><AdminAttendance /></AttendanceRoute>} />
        <Route path="admin/users" element={<StaffRoute><Users /></StaffRoute>} />
        <Route path="admin/users/:userId" element={<StaffRoute><UserDetail /></StaffRoute>} />
        <Route path="admin/inventory" element={<StaffRoute><Inventory /></StaffRoute>} />
        <Route path="admin/organization" element={<StaffRoute><Organization /></StaffRoute>} />
        <Route path="admin/payroll" element={<StaffRoute><Payroll /></StaffRoute>} />
        <Route path="admin/onboarding" element={<StaffRoute><ComingSoon title="Onboarding" /></StaffRoute>} />
        <Route path="admin/analytics" element={<HRRoute><HRAnalytics /></HRRoute>} />
        <Route path="admin/analytics/:userId" element={<HRRoute><EmployeeAttendance /></HRRoute>} />
        <Route path="admin/roles" element={<AdminRoute><Roles /></AdminRoute>} />
        <Route path="admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
        <Route path="admin/manage-leaves" element={<CapRoute anyOf={['manage_leave']}><ManageLeaves /></CapRoute>} />
        <Route path="admin/invoices" element={<AdminRoute><Invoices /></AdminRoute>} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
